import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { getAuthStatus } from 'src/api/designPlatform';
import { useExternalSystems } from 'src/hooks/useExternalSystems';
import { IntegrationType } from 'src/constants';

const POLL_INTERVAL_MS = 3000;

interface UseDesignPlatformAuthOptions {
  pollEnabled?: boolean;
}

export const useDesignPlatformAuth = (options: UseDesignPlatformAuthOptions = {}) => {
  const { pollEnabled = false } = options;
  const { data: externalSystems, isLoading: isLoadingExternalSystems } = useExternalSystems();

  const designPlatform = useMemo(
    () => externalSystems?.find((system) => system.integrationType === IntegrationType.DESIGN_PLATFORM) ?? null,
    [externalSystems],
  );

  const addonId = designPlatform?.configuredAddons[0]?.addonId ?? null;
  const platformName = addonId ? designPlatform?.name ?? null : null;

  const {
    data: authStatus,
    isLoading: isLoadingAuthStatus,
    refetch,
  } = useQuery({
    queryKey: ['design-platform-auth', addonId],
    queryFn: () => getAuthStatus(addonId as string),
    enabled: !!addonId,
    staleTime: pollEnabled ? 0 : 30000,
    refetchOnWindowFocus: true,
    refetchInterval: pollEnabled ? POLL_INTERVAL_MS : false,
    select: (response) => response.data,
  });

  return {
    addonId,
    platformName,
    isAuthenticated: authStatus?.authenticated ?? false,
    status: authStatus?.status ?? null,
    expiresAt: authStatus?.expiresAt,
    isLoading: isLoadingExternalSystems || (!!addonId && isLoadingAuthStatus),
    hasDesignPlatform: !!addonId,
    refetch,
  };
};
